/* CallChatPanel — bottom-sheet chat UI shared by visitor + resident
   during an active Scan2Call. Dumb component: state lives in
   `useCallChat`. Renders inside the fullscreen call container. */
import { useEffect, useRef, useState } from "react";
import { X, SendHorizonal, MessageSquare } from "lucide-react";

export default function CallChatPanel({ open, onClose, messages, mySender, onSend, sending }) {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const t = draft.trim();
    if (!t || sending) return;
    setDraft("");
    const ok = await onSend(t);
    if (!ok) setDraft(t); // restore on failure so nothing is lost
  };

  return (
    <div
      className="absolute inset-x-0 bottom-0 z-20 flex flex-col bg-black/90 backdrop-blur-md border-t border-white/15 rounded-t-2xl"
      style={{ maxHeight: "55%", minHeight: "40%" }}
      data-testid="call-chat-panel"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 shrink-0">
        <div className="inline-flex items-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-300 font-body">Chat</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-full flex items-center justify-center text-gray-300 hover:bg-white/10"
          aria-label="Close chat"
          data-testid="call-chat-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="call-chat-messages">
        {messages.length === 0 && (
          <p className="text-center text-xs text-gray-500 font-body pt-4" data-testid="call-chat-empty">
            No messages yet — say hello. Messages stay with this call.
          </p>
        )}
        {messages.map((m) => {
          const mine = m.sender === mySender;
          return (
            <div
              key={m.id}
              className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm font-body break-words ${
                mine
                  ? "ml-auto bg-emerald-500 text-black rounded-br-sm"
                  : "mr-auto bg-white/15 text-white rounded-bl-sm"
              }`}
              data-testid={`call-chat-msg-${m.sender}`}
            >
              {m.text}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={submit}
        className="flex items-center gap-2 px-3 pt-2 border-t border-white/10 shrink-0"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.6rem)" }}
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a message…"
          maxLength={500}
          className="flex-1 h-10 px-3 rounded-full bg-white/10 border border-white/15 text-sm text-white font-body placeholder:text-gray-500 focus:outline-none focus:border-emerald-400/60"
          data-testid="call-chat-input"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-emerald-500 text-black disabled:opacity-40 hover:bg-emerald-400 transition-colors shrink-0"
          aria-label="Send message"
          data-testid="call-chat-send"
        >
          <SendHorizonal className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
